import { resolveWeekScheduleRow, type WeekMeta, type WeekScheduleRowContext } from "../../../pages/Profile/v2/WeeklySchedule/weekScheduleCellResolver";
import type { DayEntry, WeekBlock, WeekStaffScheduleContentToggles, WeekStaffScheduleDetailToggles, WeekStaffScheduleExportLabels } from "../types";
import { buildDayEntries } from "./buildDayEntries";

type BuildWeekBlocksArgs = {
	weeks: WeekMeta[];
	rowContext: WeekScheduleRowContext;
	content: WeekStaffScheduleContentToggles;
	details: WeekStaffScheduleDetailToggles;
	labels: WeekStaffScheduleExportLabels;
	dayStart: string;
};

/** Transpose seven per-day entry lists into rectangular rows, packing each column from the top. */
const toEntryRows = (entriesByDay: DayEntry[][]): (DayEntry | undefined)[][] => {
	// A week with nothing on it still gets one row, so every block has a visible body.
	const rowCount = Math.max(1, ...entriesByDay.map((entries) => entries.length));
	return Array.from({ length: rowCount }, (_, rowIndex) => entriesByDay.map((entries) => entries[rowIndex]));
};

/**
 * Turn the grid's visible weeks into the sheet's row blocks — the whole pure core of the export.
 *
 * Cells are resolved through the same helper the grid renders from, so the sheet cannot disagree
 * with the screen about which shifts exist on which day.
 */
export const buildWeekBlocks = ({ weeks, rowContext, content, details, labels, dayStart }: BuildWeekBlocksArgs): WeekBlock[] => {
	const hasMultipleJobs = (rowContext.nurse.jobs?.length ?? 0) > 1;

	return weeks.map((week) => {
		const cells = resolveWeekScheduleRow(week, rowContext);
		const entriesByDay = cells.map((cell) => buildDayEntries({ cell, content, details, labels, dayStart, hasMultipleJobs }));
		return {
			weekStart: week.weekStart,
			dateCells: cells.map((cell) => cell.currentDate.toFormat("MM/dd")),
			entryRows: toEntryRows(entriesByDay),
		};
	});
};

/** Rows the sheet will occupy: the fixed header plus a date row and body rows per week. */
export const countExportRows = (blocks: WeekBlock[], headerRowCount: number): number =>
	blocks.reduce((total, block) => total + 1 + block.entryRows.length, headerRowCount);
